import React from "react";
import {
  VisAxis,
  VisStackedBar,
  VisTooltip,
  VisXYContainer,
} from "@unovis/react";
import styled from "styled-components";
import { Box } from "orcs-design-system";
import { StackedBar } from "@unovis/ts";
import PropTypes from "prop-types";

const CustomBox = styled(Box)`
  width: 100%;
  position: relative;
  &:before {
    content: "";
    position: absolute;
  }
`;

const SMALL_SIZE = 10;

export default function StackedBarChart({
  data,
  tooltipTemplate,
  orientation,
  height,
  barWidth,
  barColor = ["rgba(0, 123, 199, 0.7)"],
  yDirection,
  // axis props https://unovis.dev/docs/auxiliary/Axis#component-props
  tickConfig = {
    x: {},
    y: {},
  },
  getXValue,
  getYValue,
  onBarClick,
}) {
  const isSmallScreen =
    typeof window !== "undefined" && window.innerWidth < 768;

  const dynamicHeight =
    height || (isSmallScreen ? data.length * 10 : data.length * 25);
  const calculatedBarWidth =
    barWidth || (data.length > SMALL_SIZE ? 20 : undefined);

  return (
    <CustomBox>
      <VisXYContainer
        height={dynamicHeight > 400 ? dynamicHeight : 400}
        yDirection={yDirection}
      >
        <VisStackedBar
          color={barColor}
          data={data}
          barWidth={calculatedBarWidth}
          x={getXValue}
          y={getYValue}
          orientation={orientation}
          events={{
            [StackedBar.selectors.bar]: {
              click: (d) => onBarClick?.(d),
            },
          }}
        />
        <VisTooltip
          triggers={{
            [StackedBar.selectors.bar]: tooltipTemplate,
          }}
        />
        <VisAxis type="x" {...tickConfig.x} />
        <VisAxis type="y" {...tickConfig.y} />
      </VisXYContainer>
    </CustomBox>
  );
}

StackedBarChart.propTypes = {
  tooltipTemplate: PropTypes.func,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  height: PropTypes.number,
  barWidth: PropTypes.number,
  barColor: PropTypes.arrayOf(PropTypes.string),
  yDirection: PropTypes.oneOf(["north", "south"]),
  tickConfig: PropTypes.shape({
    x: PropTypes.object,
    y: PropTypes.object,
  }),
  getXValue: PropTypes.func,
  getYValue: PropTypes.func,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      comparisonValue: PropTypes.number,
    })
  ).isRequired,
  onBarClick: PropTypes.func,
};
